export type ReferenceFilterDependencyDescriptor = {
    sourcePath: string;
    watchPath: string;
    fieldPathSegments: string[];
    needsDotWalkResolution: boolean;
};

export type ReferenceFilterEntry = { path: string; value: any };

export const collectReferenceFilterEntries = (
    descriptors: ReferenceFilterDependencyDescriptor[],
    watchedDependencyValues: any,
    dotWalkData: Array<{ data?: any }>
): ReferenceFilterEntry[] => descriptors.flatMap((descriptor, index) => {
    const watchedValue = Array.isArray(watchedDependencyValues)
        ? watchedDependencyValues[index]
        : watchedDependencyValues;
    const value = descriptor.needsDotWalkResolution
        ? dotWalkData[index]?.data
        : watchedValue;
    const entries = [{ path: descriptor.sourcePath, value }];

    if (descriptor.watchPath !== descriptor.sourcePath && !descriptor.needsDotWalkResolution) {
        entries.push({ path: descriptor.watchPath, value });
    }

    return entries;
});

type PathNode = {
    children: Map<string, PathNode>;
    hasValue: boolean;
    value: any;
};

const newNode = (): PathNode => ({ children: new Map(), hasValue: false, value: undefined });

const isPlainObject = (value: any): value is Record<string, any> =>
    value !== null && typeof value === "object" && !Array.isArray(value) &&
    (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);

const materializeNode = (node: PathNode): any => {
    if (node.children.size === 0) {
        return node.value;
    }

    const result: Record<string, any> = node.hasValue
        ? isPlainObject(node.value)
            ? { ...node.value }
            : { id: node.value ?? "" }
        : {};

    node.children.forEach((child, key) => {
        result[key] = materializeNode(child);
    });
    return result;
};

export const buildReferenceFilterContext = (entries: ReferenceFilterEntry[]): Record<string, any> => {
    const root = newNode();

    entries.forEach(({ path, value }) => {
        let node = root;
        path.split(".").forEach((key) => {
            let child = node.children.get(key);
            if (!child) {
                child = newNode();
                node.children.set(key, child);
            }
            node = child;
        });
        node.hasValue = true;
        node.value = value;
    });

    const context: Record<string, any> = {};
    root.children.forEach((node, key) => {
        context[key] = materializeNode(node);
    });
    return context;
};
